import React, { useState, useEffect } from 'react';
import './ArtistNotificationBell.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get token from either key
const getAuthToken = () => {
  return localStorage.getItem('beatseek_token') || localStorage.getItem('token');
};

const ArtistNotificationBell = ({ 
  artistName, 
  artistId = null, 
  source = null, 
  artworkUrl = null,
  size = 'medium', // 'small', 'medium', 'large'
  showEmailAlert = true // Show email notification alert when following
}) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  // Check follow status on mount and when artistName changes
  useEffect(() => {
    if (artistName) {
      checkFollowStatus();
    }
  }, [artistName]);

  const checkFollowStatus = async () => {
    const token = getAuthToken();
    if (!token || !artistName) {
      console.log('🔔 No token or artistName for follow check:', { hasToken: !!token, artistName });
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/artists/follow/check/${encodeURIComponent(artistName)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔔 Follow status check for', artistName, ':', data.is_following);
        setIsFollowing(data.is_following);
      } else {
        console.log('🔔 Follow status check failed:', response.status);
      }
    } catch (error) {
      console.error('Error checking artist follow status:', error);
    }
  };

  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    const token = getAuthToken();
    if (!token) {
      alert('Please log in to follow artists');
      return;
    }

    if (isLoading) return;

    setIsLoading(true);
    setIsAnimating(true);

    console.log('🔔 Bell clicked for:', artistName, 'Currently following:', isFollowing);

    try {
      if (isFollowing) {
        // Unfollow
        console.log('🔔 Attempting to unfollow:', artistName);
        const response = await fetch(
          `${BACKEND_URL}/api/artists/follow/${encodeURIComponent(artistName)}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        console.log('🔔 Unfollow response status:', response.status);
        
        if (response.ok || response.status === 404) {
          // 404 means already not following - treat as success
          setIsFollowing(false);
          console.log('🔔 Successfully unfollowed:', artistName);
        } else {
          console.error('🔔 Unfollow failed:', response.status);
        }
      } else {
        // Follow
        console.log('🔔 Attempting to follow:', artistName);
        const response = await fetch(
          `${BACKEND_URL}/api/artists/follow`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              artist_name: artistName,
              artist_id: artistId,
              source: source,
              artwork_url: artworkUrl
            })
          }
        );

        console.log('🔔 Follow response status:', response.status);

        if (response.ok) {
          // 200 or 201 - successfully followed
          setIsFollowing(true);
          console.log('🔔 Successfully followed:', artistName);
          // Show email notification alert
          if (showEmailAlert) {
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 4000);
          }
        } else if (response.status === 400) {
          // Already following - update state to reflect reality
          console.log('🔔 Already following (400):', artistName);
          setIsFollowing(true);
          if (showEmailAlert) {
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 4000);
          }
        } else {
          console.error('🔔 Follow failed with status:', response.status);
          // Try to get error details
          try {
            const errorData = await response.json();
            console.error('🔔 Error details:', errorData);
          } catch (e) {
            // Ignore JSON parse error
          }
        }
      }
    } catch (error) {
      console.error('🔔 Error toggling artist follow:', error);
    } finally {
      setIsLoading(false);
      // Keep animation for a bit longer for visual feedback
      setTimeout(() => setIsAnimating(false), 600);
    }
  };

  const sizeClasses = {
    small: 'bell-small',
    medium: 'bell-medium',
    large: 'bell-large'
  };

  return (
    <div className="artist-notification-bell-wrapper">
      <button
        className={`artist-notification-bell ${sizeClasses[size]} ${isFollowing ? 'following' : ''} ${isAnimating ? 'ringing' : ''}`}
        onClick={handleClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        disabled={isLoading}
        data-testid={`artist-bell-${artistName?.replace(/\s+/g, '-').toLowerCase()}`}
        title={isFollowing ? `Stop notifications for ${artistName}` : `Get notified about ${artistName}`}
      >
        <svg 
          className="bell-icon" 
          viewBox="0 0 24 24" 
          fill={isFollowing ? "currentColor" : "none"}
          stroke="currentColor" 
          strokeWidth="2"
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          {isFollowing && (
            <circle cx="18" cy="5" r="3" fill="#fbbf24" stroke="none" />
          )}
        </svg>
        
        {showTooltip && (
          <span className="bell-tooltip">
            {isFollowing ? 'Following' : 'Get notified'}
          </span>
        )}
      </button>
      
      {/* Email notification alert */}
      {showAlert && (
        <div className="artist-follow-alert">
          <div className="alert-content">
            <span className="alert-icon">📧</span>
            <div className="alert-text">
              <strong>Following {artistName}!</strong>
              <p>You'll receive email notifications when this artist has new content on BEATSEEK.</p>
            </div>
            <button className="alert-close" onClick={() => setShowAlert(false)}>×</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistNotificationBell;
